import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';

const PAGE_SIZE = 50;

const initialFormState = {
  qt_ymd: '',
  title: '',
  description: '',
  thumbnail_url: '',
  video_url: '',
};

const normalizeToYmd = (value) => (value ? value.replace(/-/g, '') : '');
const normalizeToYm = (value) => (value ? value.replace(/-/g, '').slice(0, 6) : '');
const formatForDateInput = (value) =>
  value && value.length === 8 ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}` : value || '';
const getNextMonthStart = (ym) => {
  if (!ym || ym.length !== 6) return '';
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(4, 6));
  if (Number.isNaN(year) || Number.isNaN(month)) return '';
  const next = new Date(year, month, 1);
  return `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}01`;
};
const getCurrentMonthInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const getYoutubeVideoId = (url) => {
  if (!url) return '';

  const trimmedUrl = url.trim();
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /[?&]v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = trimmedUrl.match(pattern);
    if (match?.[1]) return match[1];
  }

  return '';
};

const getThumbnailUrlFromVideoUrl = (url) => {
  const videoId = getYoutubeVideoId(url);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
};

const QtVideoInfoList = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filterYm, setFilterYm] = useState(() => getCurrentMonthInput());
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    fetchVideos(normalizeToYm(getCurrentMonthInput()));
  }, []);

  const normalizedFilterYm = useMemo(() => normalizeToYm(filterYm), [filterYm]);

  const fetchVideos = async (qtYm) => {
    setLoading(true);
    setErrorMessage('');
    try {
      let query = supabase
        .from('qt_video_youtube')
        .select('*')
        .order('qt_ymd', { ascending: false })
        .order('created_at', { ascending: false });

      if (qtYm) {
        const startYmd = `${qtYm}01`;
        const nextMonthStart = getNextMonthStart(qtYm);
        query = query.gte('qt_ymd', startYmd);
        if (nextMonthStart) query = query.lt('qt_ymd', nextMonthStart);
      }

      const { data, error } = await query;
      if (error) throw error;
      setVideos(data ?? []);
      setSelectedIds([]);
    } catch (error) {
      setErrorMessage(error.message || '데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchVideos(normalizedFilterYm);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(videos.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: nextValue,
      };

      if (name === 'video_url') {
        const videoId = getYoutubeVideoId(String(nextValue || ''));
        next.thumbnail_url = videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
      }

      return next;
    });
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const qtYmd = normalizeToYmd(formData.qt_ymd);
    const videoUrl = String(formData.video_url || '').trim();
    const thumbnailUrl = formData.thumbnail_url || getThumbnailUrlFromVideoUrl(videoUrl);
    const title = String(formData.title || '').trim() || `QT ${qtYmd}`;

    if (!qtYmd || !videoUrl) {
      setErrorMessage('QT 일자와 영상 URL은 필수입니다.');
      return;
    }

    if (!thumbnailUrl) {
      setErrorMessage('유효한 YouTube 영상 URL을 입력해주세요.');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    try {
      const now = new Date().toISOString();
      const payload = {
        qt_ymd: qtYmd,
        title,
        description: formData.description || '',
        thumbnail_url: thumbnailUrl,
        video_url: videoUrl,
        updated_at: now,
      };

      if (editingId) {
        const { data, error } = await supabase
          .from('qt_video_youtube')
          .update(payload)
          .eq('id', editingId)
          .select();

        if (error) throw error;
        if (!data?.length) {
          throw new Error('수정된 데이터가 없습니다. 권한을 확인해주세요.');
        }
      } else {
        const { error } = await supabase
          .from('qt_video_youtube')
          .insert([{ ...payload, created_at: now }]);

        if (error) throw error;
      }

      resetForm();
      setShowForm(false);
      fetchVideos(normalizedFilterYm || undefined);
    } catch (error) {
      setErrorMessage(error.message || '동영상 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (video) => {
    const videoUrl = video.video_url || '';
    const qtYmd = video.qt_ymd || '';

    setShowForm(true);
    setEditingId(video.id);
    setFormData({
      qt_ymd: formatForDateInput(qtYmd),
      title: video.title?.trim() || `QT ${qtYmd}`,
      description: video.description || '',
      thumbnail_url: video.thumbnail_url || getThumbnailUrlFromVideoUrl(videoUrl),
      video_url: videoUrl,
    });
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm('선택한 동영상을 삭제하시겠습니까?')) return;

    setLoading(true);
    setErrorMessage('');
    try {
      const { error } = await supabase
        .from('qt_video_youtube')
        .delete()
        .in('id', selectedIds);
      if (error) throw error;

      fetchVideos(normalizedFilterYm || undefined);
    } catch (error) {
      setErrorMessage(error.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex align-items-center justify-content-between">
        <h6 className="m-0 font-weight-bold text-primary">동영상 관리</h6>
        <div>
          <button className="btn btn-primary btn-sm mr-2" onClick={handleSearch} disabled={loading}>
            조회
          </button>
          <button
            className="btn btn-success btn-sm mr-2"
            onClick={() => {
              setShowForm((prev) => {
                if (prev) {
                  resetForm();
                }
                return !prev;
              });
            }}
          >
            {showForm ? '입력 닫기' : '추가'}
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDelete}
            disabled={!selectedIds.length || loading}
          >
            삭제
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="mb-3">
          <label className="font-weight-bold mr-2">검색년월</label>
          <input
            type="month"
            className="form-control w-auto d-inline-block mr-2"
            value={filterYm}
            onChange={(event) => setFilterYm(event.target.value)}
          />
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        {showForm && (
          <div className="border rounded p-3 mb-4">
            <h6 className="font-weight-bold mb-3">동영상 추가</h6>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group col-md-3">
                  <label>QT 일자</label>
                  <input
                    type="date"
                    name="qt_ymd"
                    className="form-control"
                    value={formData.qt_ymd}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group col-md-5">
                  <label>제목</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>영상 URL</label>
                  <input
                    type="url"
                    name="video_url"
                    className="form-control"
                    value={formData.video_url}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>설명</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows="2"
                  value={formData.description}
                  onChange={handleFormChange}
                ></textarea>
              </div>

              <div className="form-group">
                <label>썸네일 URL</label>
                <input
                  type="text"
                  name="thumbnail_url"
                  className="form-control"
                  value={formData.thumbnail_url}
                  readOnly
                  placeholder="영상 URL 입력 시 자동 생성됩니다."
                />
              </div>

              <div className="border rounded p-2 mb-3 bg-light">
                <small className="text-muted">
                  현재 테이블은 `qt_video_youtube`를 사용하며 영상 유형/활성 컬럼은 없습니다.
                </small>
              </div>

              <button type="submit" className="btn btn-success mr-2" disabled={saving}>
                {saving ? '저장 중...' : editingId ? '수정 완료' : '저장'}
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
                  새로 추가
                </button>
              )}
            </form>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="thead-light">
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === videos.length && videos.length > 0}
                    onChange={(event) => handleSelectAll(event.target.checked)}
                  />
                </th>
                <th>QT 일자</th>
                <th>제목</th>
                <th>설명</th>
                <th>썸네일</th>
                <th>영상 링크</th>
                <th>수정일</th>
                <th style={{ width: '90px' }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    로딩 중...
                  </td>
                </tr>
              ) : videos.length ? (
                videos.map((video) => (
                  <tr key={video.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(video.id)}
                        onChange={() => handleCheckboxChange(video.id)}
                      />
                    </td>
                    <td>{video.qt_ymd}</td>
                    <td>{video.title}</td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {video.description}
                    </td>
                    <td>
                      {video.thumbnail_url ? (
                        <a href={video.thumbnail_url} target="_blank" rel="noreferrer" title="원본 썸네일 열기">
                          <img
                            src={video.thumbnail_url}
                            alt={`${video.title || 'QT 영상'} 썸네일`}
                            style={{
                              width: '72px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6',
                            }}
                          />
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <a href={video.video_url} target="_blank" rel="noreferrer">
                        링크 이동
                      </a>
                    </td>
                    <td>{new Date(video.updated_at || video.created_at).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleEditClick(video)}
                      >
                        수정
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    조회된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QtVideoInfoList;
